import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';
import { ResponseModel } from '@dental-ui/account/models';
import { ChatGroupMessagePayload } from './chat.model';

@Injectable({
  providedIn: 'root',
})
export class ChatApiService {
  private readonly http = inject(HttpClient);

  /**
   * Tüm sohbetlerin listesini yükler.
   */
  getAll(page = 1, numberOfRecord = 150600): Observable<ResponseModel<unknown>> {
    const params = new HttpParams()
      .set('Page', String(page))
      .set('NumberOfRecord', String(numberOfRecord));
    return this.http.get<ResponseModel<unknown>>(buildApiUrl('chat.getAll'), {
      headers: buildRequestHeaders(true),
      params,
    });
  }

  getMessages(groupName: string, entityId?: string, entityType?: number): Observable<ResponseModel<unknown>> {
    let params = new HttpParams().set('groupName', groupName);
    if (entityId) {
      params = params.set('entityId', entityId);
    }
    if (entityType !== undefined) {
      params = params.set('entityType', String(entityType));
    }
    return this.http.get<ResponseModel<unknown>>(buildApiUrl('chat.getByGroupName'), {
      headers: buildRequestHeaders(true),
      params,
    });
  }

  /**
   * Yeni sohbet oluşturmak için POST isteği gönderir (ilk sohbet).
   */
  postMessage(payload: ChatGroupMessagePayload): Observable<ResponseModel<unknown>> {
    return this.http.post<ResponseModel<unknown>>(buildApiUrl('chat.post'), payload, {
      headers: buildRequestHeaders(true),
    });
  }

  /**
   * Sohbet odasından mesaj göndermek için POST isteği gönderir (SignalR tetiklemeli).
   */
  sendMessageToGroup(payload: ChatGroupMessagePayload, file?: File | null): Observable<ResponseModel<unknown>> {
    const formData = new FormData();

    const appendObject = (obj: Record<string, string | number | boolean | null | undefined> | undefined, prefix: string) => {
      if (!obj) return;
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        formData.append(`${prefix}.${key}`, val !== null && val !== undefined ? String(val) : '');
      }
    };

    appendObject(payload.author as unknown as Record<string, string | number | boolean | null | undefined>, 'Author');
    appendObject(payload.chatListTargetAuthor as unknown as Record<string, string | number | boolean | null | undefined>, 'ChatListTargetAuthor');

    formData.append('GroupName', payload.groupName || '');
    formData.append('Title', payload.title || '');
    formData.append('Message', payload.message || '');
    formData.append('EntityType', payload.entityType !== undefined ? String(payload.entityType) : '1');
    formData.append('EntityId', payload.entityId || '');

    if (file) {
      formData.append('File', file, file.name);
    }

    // Content-Type başlığını tarayıcının boundary eklemesi için kaldırıyoruz.
    let headers = buildRequestHeaders(true);
    headers = headers.delete('Content-Type');

    return this.http.post<ResponseModel<unknown>>(buildApiUrl('chat.sendMessageToGroup'), formData, {
      headers,
    });
  }

  /**
   * Belirli bir communicationId değerine sahip ChatUser kaydını getirir.
   */
  getUserByUserName(communicationId: string): Observable<ResponseModel<unknown>> {
    const params = new HttpParams().set('chatUser', communicationId);
    return this.http.get<ResponseModel<unknown>>(buildApiUrl('chat.getUserByUserName'), {
      headers: buildRequestHeaders(true),
      params,
    });
  }

  /**
   * Sohbet grubundaki okunmamış mesajları okundu yapar.
   */
  markAllRead(groupName: string, communicationId: string): Observable<ResponseModel<unknown>> {
    const payload = { groupName, communicationId };
    return this.http.put<ResponseModel<unknown>>(buildApiUrl('chat.markAllRead'), payload, {
      headers: buildRequestHeaders(true),
    });
  }
}
